import React, { ComponentType } from 'react';
import cx from 'classnames';

import PrimaryLink from '../PrimaryLink';
import IconButton from '../IconButton';
import ChevronRightS1 from '../Icons/ChevronRightS1';
import ChevronUpS1 from '../Icons/ChevronUpS1';
import ChevronDownS1 from '../Icons/ChevronDownS1';
import { useNavContext } from './NavigationContext';

import styles from './NavigationItem.module.scss';

export interface NavigationItemProps {
  /** Children that will be displayed in an accordion if `variant="expandable"`.  */
  children?: React.ReactNode;
  /** Apply CSS class to the container. */
  className?: Parameters<typeof cx>[0];
  /** The icon that appears before the `label`. */
  icon?: React.ReactElement;
  /** If `true`, will apply the active state to `Navigation.Item`. */
  isActive?: boolean;
  /** If `true` and `variant="expandable"`, will show nested items. */
  isExpanded?: boolean;
  /** The label for the `Navigation.Item`. */
  label: string;
  /** The function that will be called when the `Navigation.Item` is clicked. This will only apply for `variant="default"`, `variant="expandable"`, or `variant="drillIn"`. */
  onClick?: () => void;
  /** If `variant="default"`, will navigate to the destination route when clicked. */
  to?: string;
  /** Select between different interactive types of `Navigation.Item`.  */
  variant?: 'default' | 'nestedLink' | 'expandable' | 'drillIn';
  /** Component that will wrap around `Navigation.Item`. In most cases, it would be `Link` from React Router or any other framework. */
  component?: ComponentType;
}

export const NavigationItem: React.FC<NavigationItemProps> = ({
  children,
  className,
  component,
  icon,
  isActive = false,
  isExpanded,
  label,
  onClick,
  to,
  variant = 'default',
}) => {
  const { isCollapsed, toggleCollapse } = useNavContext();

  if (isCollapsed && icon) {
    return (
      <IconButton
        accessibilityLabel={label}
        icon={icon}
        className={cx(
          styles.navItem,
          isActive && styles.isActive,
          isCollapsed && styles.isCollapsed,
        )}
        to={to}
        component={to && component ? component : undefined}
        wrapperClassName={styles.navItemBtnWrapper}
        tooltipPlacement='right'
        onClick={() => {
          if (onClick && typeof onClick == 'function') {
            onClick();
          }
          if (variant !== 'default') {
            toggleCollapse();
          }
          return true;
        }}
      />
    );
  }

  if (variant === 'drillIn') {
    return (
      <PrimaryLink
        icon={icon}
        iconClassName={styles.navItemPrefixIcon}
        className={cx(
          styles.navItem,
          styles.navItemBtn,
          isActive && styles.isActive,
          className,
        )}
        onClick={onClick}
      >
        <div className={styles.navItemWrapper}>
          {label}
          <div className={styles.navItemSuffix}>
            <ChevronRightS1 />
          </div>
        </div>
      </PrimaryLink>
    );
  }

  if (variant === 'expandable') {
    return (
      <>
        <PrimaryLink
          icon={icon}
          iconClassName={styles.navItemPrefixIcon}
          className={cx(
            styles.navItem,
            styles.navItemBtn,
            isActive && styles.isActive,
            isExpanded && styles.isExpanded,
            className,
          )}
          onClick={onClick}
        >
          <div className={styles.navItemWrapper}>
            {label}
            <div className={styles.navItemSuffix}>
              {isExpanded ? <ChevronUpS1 /> : <ChevronDownS1 />}
            </div>
          </div>
        </PrimaryLink>
        {isExpanded && children}
      </>
    );
  }

  if (variant === 'nestedLink') {
    return (
      <PrimaryLink
        component={component}
        to={to}
        hasIcon={false}
        iconClassName={styles.navItemPrefixIcon}
        className={cx(
          styles.navItem,
          styles.navItemNestedLink,
          to && isActive && styles.isActive,
          className,
        )}
      >
        {label}
      </PrimaryLink>
    );
  }
  return (
    <PrimaryLink
      component={component}
      to={to}
      icon={icon ? icon : undefined}
      iconClassName={styles.navItemPrefixIcon}
      hasIcon={false}
      className={cx(
        styles.navItem,
        styles.navItemLink,
        to && isActive && styles.isActive,
        className,
      )}
    >
      {label}
    </PrimaryLink>
  );
};

export default NavigationItem;
export { styles };
